/* eslint-disable no-mixed-spaces-and-tabs */
/* eslint-disable react/jsx-wrap-multilines */
import React, { useCallback, useEffect } from 'react';
import { useLocation, useHistory } from 'react-router';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { createStructuredSelector } from 'reselect';
import reducer from './reducer';
import { useInjectReducer } from '../../utils/injectReducer';
import { useInjectSaga } from '../../utils/injectSaga';
import saga from './saga';
import { getFiltersAction, getTransportsListAction, resetStateAction } from './actions';
import {
	getFiltersSelector,
	getIsFiltersLoadingSelector,
	getIsTransportsListLoadingSelector,
	getPaginationSelector,
	getTransportsListSelector,
} from './selectors';
import { PageContentWithNav } from '../../components/page-content-with-nav';
import { headerTextMap, TABLE_COLUMNS, TRANSPORTS_TABS } from './constants';
import { removeModalQuery } from '../../utils/removeModalQuery';
import { DataGridComponent } from '../../components/data-grid';
import { normalizeDataGridModel } from '../../utils/normalizeDataGridModel';

const TransportContainerWrapper = ({
	onGetTransportsList,
	transportsList,
	onResetState,
	isTransportsListLoading,
	pagination,
	onGetFilters,
	filters,
}) => {
	useInjectSaga({ key: 'transportsContainerSaga', saga });
	useInjectReducer({ key: 'transportsContainerReducer', reducer });

	const { search, pathname } = useLocation();

	const query = new URLSearchParams(search);

	const history = useHistory();

	const type = pathname.split('/')[2];

	useEffect(() => {
		onGetTransportsList({ type, searchQuery: removeModalQuery(query.toString()) });
		onGetFilters({ type });

		return () => onResetState();
	}, [pathname]);

	const handlePageChange = (_, { activePage }) => {
		query.set('page', activePage);
		history.push(`${pathname}?${query}`);
		onGetTransportsList({ type, searchQuery: query.toString() });
	};

	const handleSelectChange = (_, { value }) => {
		query.set('page', 1);
		if (value === 'all') {
			query.delete('per_page');
		} else {
			query.set('per_page', value);
		}
		history.push(`${pathname}?${query}`);
		onGetTransportsList({ type, searchQuery: query.toString() });
	};

	const headerText = headerTextMap[type] || headerTextMap.default;

	const handleSortChange = useCallback(sortModel => {
		const jsonSorts = JSON.stringify(sortModel);

		query.set('sorts', jsonSorts);

		history.push(`${pathname}?${query}`);
		onGetTransportsList({ type, searchQuery: query.toString() });
	});

	const handleFilterChange = useCallback((filterModel, action) => {
		if (action.reason) {
			const jsonFilters = JSON.stringify(normalizeDataGridModel(filterModel, TABLE_COLUMNS(type, filters)));

			query.delete('page');
			query.set('filters', jsonFilters);

			history.push(`${pathname}?${query}`);
			onGetTransportsList({ type, searchQuery: query.toString() });
		}
	});

	return (
		<PageContentWithNav
			pageNavigationItems={TRANSPORTS_TABS}
			pageContent={
				<DataGridComponent
					keyTable={type}
					headerText={headerText}
					columns={TABLE_COLUMNS(type, filters)}
					rows={transportsList}
					pagination={
						!pagination
							? null
							: {
									...pagination,
									onPageChange: handlePageChange,
									onSelectChange: handleSelectChange,
							  }
					}
					isTableDataLoading={isTransportsListLoading}
					handleSortChange={handleSortChange}
					handleFilterChange={handleFilterChange}
				/>
			}
		/>
	);
};

const mapStateToProps = createStructuredSelector({
	transportsList: getTransportsListSelector(),
	isTransportsListLoading: getIsTransportsListLoadingSelector(),
	pagination: getPaginationSelector(),
	filters: getFiltersSelector(),
	isFiltersLoading: getIsFiltersLoadingSelector(),
});

const mapDispatchToProps = {
	onGetTransportsList: getTransportsListAction,
	onGetFilters: getFiltersAction,
	onResetState: resetStateAction,
};

const TransportsContainer = connect(mapStateToProps, mapDispatchToProps)(TransportContainerWrapper);

TransportContainerWrapper.propTypes = {
	onGetTransportsList: PropTypes.func.isRequired,
	transportsList: PropTypes.array,
	isTransportsListLoading: PropTypes.bool.isRequired,
	onGetFilters: PropTypes.func.isRequired,
	filters: PropTypes.object,
	isFiltersLoading: PropTypes.bool.isRequired,
	pagination: PropTypes.shape({
		currentPage: PropTypes.number,
		totalPages: PropTypes.number.isRequired,
		total: PropTypes.number,
		from: PropTypes.number,
		to: PropTypes.number,
		isNextItem: PropTypes.bool,
		isPrevItem: PropTypes.bool,
		isFirstItem: PropTypes.bool,
		isLastItem: PropTypes.bool,
	}),
	onResetState: PropTypes.func.isRequired,
	tabs: PropTypes.arrayOf(PropTypes.object),
};

export default TransportsContainer;
